import { format } from 'date-fns';

/**
 * Returns true when a voter's name is withheld for privacy reasons.
 * Some states (e.g. UT) redact voter names; in those cases both
 * first_name and last_name come back as blank/null from voters_comb.
 */
export const isVoterNamePrivate = (firstName, lastName) => !firstName?.trim() && !lastName?.trim();

/**
 * Returns the voter's full display name, or the string "Private" when
 * the name has been withheld for privacy reasons.
 */
export const formatVoterName = (firstName, lastName) => {
  if (isVoterNamePrivate(firstName, lastName)) return 'Private';
  return `${firstName || ''} ${lastName || ''}`.trim();
};

/**
 * Title-cases a string that may arrive ALL CAPS from the voter file
 * (e.g. "DANIEL SILVER" -> "Daniel Silver", "ADA" -> "Ada"). Non-strings
 * are returned untouched.
 */
export const toTitleCase = (str) =>
  typeof str === 'string' ? str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : str;

const normalizeAddress = (address) => (address ?? '').trim().replace(/\s+/g, ' ').toUpperCase();

/**
 * The registration unit line. A blank reg_address2 falls back to the mailing
 * unit, but only when the mailing street matches the registration street, so
 * a PO box or elsewhere mailing address cannot invent a unit. Mirrors the
 * app's unitAddress2 (right-impact-app src/data/types/voter.ts).
 */
export const unitAddress2 = (voter) => {
  const regAddress2 = voter?.reg_address2?.trim();
  if (regAddress2) return regAddress2;
  const mailAddress1 = normalizeAddress(voter?.mail_address1);
  const mailAddress2 = voter?.mail_address2?.trim();
  return mailAddress2 && mailAddress1 && mailAddress1 === normalizeAddress(voter?.reg_address1)
    ? mailAddress2
    : '';
};

/**
 * Expands a voter-file sex code ("M"/"F") to a display word. Unknown values
 * are returned as-is (or null when empty).
 */
export const formatSex = (sex) => {
  const s = (sex || '').trim().toUpperCase();
  if (s === 'M' || s === 'MALE') return 'Male';
  if (s === 'F' || s === 'FEMALE') return 'Female';
  return sex || null;
};

/**
 * Strict display variant of formatSex for user-facing prose/labels: returns
 * "Male"/"Female" only for recognized codes, and null for anything else
 * (unknown code, blank, non-binary). Callers should omit the value or use a
 * neutral fallback rather than leak a raw code (e.g. "is a u").
 */
export const displaySex = (sex) => {
  const s = formatSex(sex);
  return s === 'Male' || s === 'Female' ? s : null;
};

/**
 * Whether the voters_comb `children` flag indicates children in the household.
 * The column arrives in mixed encodings ("Y"/"YES"/"TRUE", "N"/"NO"/"FALSE",
 * or a numeric count). Kept in sync with VoterHousehold's fmtChildren display
 * so the persona summary and the Household card never disagree.
 */
export const isChildrenPresent = (v) => {
  if (v == null || v === '') return false;
  const s = String(v).trim().toUpperCase();
  if (['Y', 'YES', 'TRUE'].includes(s)) return true;
  if (['N', 'NO', 'FALSE'].includes(s)) return false;
  const n = Number(s);
  return Number.isFinite(n) && n > 0;
};

/**
 * Formats a voter-file date of birth (stored as an 8-char "MMDDYYYY" string)
 * as "MMM dd, yyyy". Returns null for missing or malformed values.
 */
export const formatVoterDob = (dob) => {
  if (!dob || dob.length !== 8) return null;
  const month = parseInt(dob.substring(0, 2), 10);
  const day = parseInt(dob.substring(2, 4), 10);
  const year = parseInt(dob.substring(4, 8), 10);
  if (day < 1 || day > 31 || month < 1 || month > 12 || year < 1900) return null;
  return format(new Date(year, month - 1, day), 'MMM dd, yyyy');
};
